import { z } from 'zod'

/**
 * A BCP 47 language tag as the rest of the product uses it: `pl`, `en`, `de`.
 *
 * Kept as a plain string rather than an enum because the set is per restaurant
 * and comes from the backend (`GET /restaurants/{id}/languages`). Freezing a
 * list here would mean a venue enabling a new language in the menu could not use
 * it on its website.
 */
export const languageCodeSchema = z
  .string()
  .min(2)
  .max(8)
  .regex(/^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/, 'invalid language code')

/**
 * One translatable value, keyed by language.
 *
 * A record rather than the `translations: [{ languageCode, name }]` array the
 * menu API speaks. The array shape is a transport concern and it allows the same
 * language twice, which a document must not; here a lookup by language is the
 * operation every renderer performs on every field, and a record does it without
 * a scan. Mapping between the two belongs in the API layer.
 *
 * Empty for a language simply means "not translated yet". The renderer falls
 * back to the site's default language rather than rendering nothing, and
 * publication of a barely translated language is refused separately.
 */
export function localized<T extends z.ZodTypeAny>(value: T) {
  return z.record(languageCodeSchema, value)
}

export const localizedTextSchema = localized(z.string())

export type LanguageCode = z.infer<typeof languageCodeSchema>
export type Localized<T> = Partial<Record<string, T>>
